import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";

function placeMenu(node, minWidth, prefer) {
  const rect = node.getBoundingClientRect();
  const margin = 12;
  const width = Math.min(Math.max(rect.width, minWidth), window.innerWidth - margin * 2);
  let left = rect.left;
  if (left + width > window.innerWidth - margin) left = Math.max(margin, window.innerWidth - margin - width);
  const spaceBelow = window.innerHeight - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  const openUp = spaceBelow < prefer && spaceAbove > spaceBelow;
  const maxHeight = Math.max(160, Math.min(320, (openUp ? spaceAbove : spaceBelow) - 8));
  return {
    position: "fixed",
    left,
    width,
    maxHeight,
    top: openUp ? "auto" : rect.bottom + 6,
    bottom: openUp ? window.innerHeight - rect.top + 6 : "auto",
    right: "auto",
    zIndex: 40,
  };
}

export function MenuField({label, value, onChange, options, testId, buttonLabel}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [box, setBox] = useState(null);
  const root = useRef(null);
  const search = useRef(null);
  const optionsRef = useRef(options);
  const valueRef = useRef(value);
  optionsRef.current = options;
  valueRef.current = value;
  const chosen = options.find(option => option.value === value);
  const searchable = options.length > 6;
  const shown = options.filter(option => option.label.toLowerCase().includes(query.trim().toLowerCase()));

  useLayoutEffect(() => {
    if (!open || !root.current) return undefined;
    const place = () => setBox(placeMenu(root.current, 260, 160));
    place();
    const onPointer = (event) => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", onPointer);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setCursor(Math.max(0, optionsRef.current.findIndex(option => option.value === valueRef.current)));
    if (optionsRef.current.length > 6) requestAnimationFrame(() => search.current?.focus());
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open]);

  useEffect(() => {
    if (query) setCursor(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    root.current?.querySelector(".menu-option.active")?.scrollIntoView({block: "nearest"});
  }, [cursor, open, shown.length]);

  const choose = (option) => { onChange(option.value); setOpen(false); };
  const onKey = (event) => {
    if (!open && (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); setOpen(false); return; }
    if (event.key === "ArrowDown") { event.preventDefault(); setCursor(index => Math.min(shown.length - 1, index + 1)); return; }
    if (event.key === "ArrowUp") { event.preventDefault(); setCursor(index => Math.max(0, index - 1)); return; }
    if (event.key === "Enter" && shown[cursor]) { event.preventDefault(); choose(shown[cursor]); }
  };

  return <label className={`menu-field${open ? " open" : ""}`} ref={root}>
    {label}
    <button type="button" className={open ? "menu-trigger open" : "menu-trigger"} aria-haspopup="listbox" aria-expanded={open} data-testid={testId} onClick={() => setOpen(current => !current)} onKeyDown={onKey}>
      <span>{buttonLabel || chosen?.label || "Choose"}</span>
      <ChevronDown size={16}/>
    </button>
    {open && box && <div className="menu-panel anchored" role="listbox" style={box}>
      {searchable && <div className="menu-search"><Search size={14}/><input ref={search} value={query} onChange={event => setQuery(event.target.value)} onKeyDown={onKey} placeholder="Search" aria-label={`Search ${label}`} data-testid={`${testId}-search`}/></div>}
      <div className="menu-options">
        {shown.length === 0 && <p className="menu-empty">{options.length === 0 ? "Nothing to choose yet." : "Nothing matches."}</p>}
        {shown.map((option, index) => <button type="button" role="option" aria-selected={option.value === value} key={`${option.value}-${option.label}`} className={["menu-option", option.value === value ? "selected" : "", index === cursor ? "active" : ""].filter(Boolean).join(" ")} onMouseEnter={() => setCursor(index)} onClick={() => choose(option)}>
          <span>{option.label}</span>
          {option.value === value && <Check size={14}/>}
        </button>)}
      </div>
    </div>}
  </label>;
}

function isoDay(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
function parseIso(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) };
}
function prettyDate(value) {
  const parts = parseIso(value);
  if (!parts) return "";
  return new Date(parts.year, parts.month, parts.day).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export function DateField({ label, value, onChange, testId, min }) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState(null);
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const root = useRef(null);

  useLayoutEffect(() => {
    if (!open || !root.current) return undefined;
    const place = () => setBox(placeMenu(root.current, 280, 320));
    place();
    const onPointer = (event) => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", onPointer);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const shown = parseIso(value) || parseIso(min);
    const now = new Date();
    setCursor(shown ? { year: shown.year, month: shown.month } : { year: now.getFullYear(), month: now.getMonth() });
  }, [open, value, min]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open]);

  const shift = (amount) => setCursor(current => {
    const next = new Date(current.year, current.month + amount, 1);
    return { year: next.getFullYear(), month: next.getMonth() };
  });
  const days = new Date(cursor.year, cursor.month + 1, 0).getDate();
  const pad = (new Date(cursor.year, cursor.month, 1).getDay() + 6) % 7;
  const now = new Date();
  const todayIso = isoDay(now.getFullYear(), now.getMonth(), now.getDate());
  const title = new Date(cursor.year, cursor.month, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const pick = (day) => {
    const iso = isoDay(cursor.year, cursor.month, day);
    if (min && iso < min) return;
    onChange(iso);
    setOpen(false);
  };

  return <label className={`menu-field date-field${open ? " open" : ""}`} ref={root}>
    {label}
    <button type="button" className={open ? "menu-trigger open" : "menu-trigger"} aria-haspopup="dialog" aria-expanded={open} data-testid={testId} onClick={() => setOpen(current => !current)}>
      <span className={value ? "" : "date-placeholder"}>{value ? prettyDate(value) : "Choose a date"}</span>
      <CalendarDays size={16}/>
    </button>
    {open && box && <div className="menu-panel date-panel anchored" role="dialog" aria-label={label} style={box}>
      <div className="date-head">
        <button type="button" className="icon-button" onClick={() => shift(-1)} aria-label="Previous month" data-testid={`${testId}-prev`}><ChevronLeft size={16}/></button>
        <strong>{title}</strong>
        <button type="button" className="icon-button" onClick={() => shift(1)} aria-label="Next month" data-testid={`${testId}-next`}><ChevronRight size={16}/></button>
      </div>
      <div className="date-grid">
        {["M", "T", "W", "T", "F", "S", "S"].map((name, index) => <span key={name + index}>{name}</span>)}
        {Array.from({ length: pad }, (_, index) => <i key={`pad-${index}`}/>)}
        {Array.from({ length: days }, (_, index) => {
          const day = index + 1;
          const iso = isoDay(cursor.year, cursor.month, day);
          const disabled = Boolean(min && iso < min);
          const weekend = (pad + index) % 7 > 4;
          const isStart = Boolean(min && iso === min && iso !== value);
          const className = ["date-day", iso === value ? "selected" : "", iso === todayIso && iso !== value ? "today" : "", weekend ? "weekend" : "", disabled ? "disabled" : "", isStart ? "range-start" : ""].filter(Boolean).join(" ");
          return <button type="button" key={iso} className={className} disabled={disabled} onClick={() => pick(day)} data-testid={`${testId}-day-${iso}`}>{day}</button>;
        })}
      </div>
    </div>}
  </label>;
}
